import { useCallback, useState } from 'react';
import { WithdrawalReportStatusDataType } from 'commons/services/withdrawalReportService';
import { ReportStatus } from 'commons/types';
import { nextReportAttemptId } from './reportAttemptId';
import { useCreateWithdrawalPdfReportMutation } from './useCreateWithdrawalPdfReportMutation';
import { useGetWithdrawalReportStatusPolling } from './useGetWithdrawalReportStatusPolling';

interface UseWithdrawalPdfReportGenerationProps {
  onError?: () => void;
}

export function useWithdrawalPdfReportGeneration({
  onError,
}: UseWithdrawalPdfReportGenerationProps = {}) {
  const [requestId, setRequestId] = useState<number | undefined>();
  const [report, setReport] = useState<
    WithdrawalReportStatusDataType | undefined
  >();
  const [attemptId, setAttemptId] = useState<number | undefined>(undefined);

  const createReportMutation = useCreateWithdrawalPdfReportMutation({
    onSuccess: (requestId) => {
      setRequestId(requestId);
    },
    onError,
  });

  const { isPolling } = useGetWithdrawalReportStatusPolling({
    requestId,
    attemptId,
    onFinished: ({ status, temporaryDownloadUrl }) => {
      setRequestId(undefined);
      setReport({ status, temporaryDownloadUrl });
      if (status === ReportStatus.Failed) {
        onError?.();
      }
    },
    onError: () => {
      setRequestId(undefined);
      setReport(undefined);
      onError?.();
    },
  });

  const { mutate: createReport } = createReportMutation;

  const startReportGeneration = useCallback(
    (withdrawalId: number) => {
      setReport(undefined);
      setAttemptId(nextReportAttemptId());
      createReport(withdrawalId);
    },
    [createReport],
  );

  const isLoading = isPolling || createReportMutation.isPending;

  return {
    startReportGeneration,
    isLoading,
    report,
  };
}
